/**
 * Datos de fallback que reproducen el demo cuando Supabase no está configurado.
 * Cuando se asigna el proyecto Supabase y se llena .env, estos datos quedan
 * sólo como respaldo y la app usa la BD real.
 *
 * Las imágenes vienen de /img/products/ (servidas por Vite/Nginx).
 */
import type { Product, Vacancy } from './database.types';

interface DemoSede {
  id: string;
  nombre: string;
  direccion: string | null;
  telefono: string | null;
  whatsapp: string | null;
  horario: string | null;
  icon: string | null;
  maps_embed_url: string | null;
  maps_link_url: string | null;
  badge: string | null;
  is_active: boolean;
  sort_order: number;
}

// Productos activos del catálogo (los dados de baja se quitaron de aquí y se
// marcan is_active=false en la BD / seed).
export const DEMO_PRODUCTS: Product[] = [
  // ─── Piernas y Jamones ─────────────────────────────────────
  mk('el-dorado',          'Jamón El Dorado',          'piernas',  'el-dorado.jpg',          'POPULAR',  10),
  mk('jamon-pavo-horneado','Jamón de Pavo Horneado',   'piernas',  'jamon-cocido.jpg',        null,      30),
  mk('jamon-york',         'Jamón York',               'piernas',  'jamon-york.jpg',          null,      40),
  mk('jamon-cocido',       'Jamón Cocido',             'piernas',  'jamon-cocido.jpg',        null,      50),
  mk('jamon-virginia',     'Jamón Virginia',           'piernas',  'jamon-virginia.jpg',      null,      60),
  mk('pierna-imperial',    'Tocino Ahumado Imperial',  'piernas',  'pierna-imperial.jpg',     'PREMIUM', 70),
  mk('pierna-honey',       'Pierna Española',          'piernas',  'pierna-honey.jpg',        'ESPECIAL',80),
  // ─── Chorizos ───────────────────────────────────────────────
  mk('chorizo-especial',    'Chorizo Especial',        'chorizos', 'chorizo-especial.jpg',   'POPULAR', 10),
  mk('chistorra',           'Chistorra',               'chorizos', 'chistorra.jpg',           null,     30),
  mk('longaniza-fresca',    'Chorizo Argentino',       'chorizos', 'longaniza-fresca.jpg',    null,     50),
  // ─── Embutidos ──────────────────────────────────────────────
  mk('espaldilla',          'Espaldilla Cocida de Cerdo','embutidos','espaldilla.jpg',        null,     10),
  mk('mortadela',           'Mortadela',               'embutidos','mortadela.jpg',           null,     20),
  mk('aris-americano',      'Jamón Aris Americano',    'embutidos','aris-americano.jpg',     'POPULAR', 40),
];

function mk(
  id: string,
  name: string,
  category: Product['category'],
  imgFile: string,
  badge: string | null,
  sort_order: number,
): Product {
  return {
    id,
    name,
    category,
    description: null,
    image_path: `/img/products/${imgFile}`,
    badge,
    price_retail: null,
    price_wholesale: null,
    is_active: true,
    sort_order,
    created_at: '2024-01-01T00:00:00Z',
  };
}

// Sedes activas (Nicolás Romero se quitó: es la misma ubicación que Empacadora).
export const DEMO_SEDES: DemoSede[] = [
  {
    id: 's2',
    nombre: 'Frigoríficos Las Polvillas',
    direccion: 'Planta TIF 507 · Carr. Cuautitlán–Teoloyucan Km 5.5 S/N, Ex Hacienda San José, 54770 Teoloyucan, Méx.',
    telefono: '525511288544',
    whatsapp: '525653712507',
    horario: 'Lun–Vie: 8:00am – 6:00pm · Distribuidores con cita previa',
    icon: 'warehouse',
    maps_embed_url: 'https://maps.google.com/maps?q=Planta+TIF+507+Carretera+Cuautitlan+Teoloyucan+Km+5.5+Ex+Hacienda+San+Jose+Teoloyucan&output=embed&z=15',
    maps_link_url:  'https://maps.app.goo.gl/XYoMaLLFPrjDY2gT6?g_st=iw',
    badge: null,
    is_active: true,
    sort_order: 20,
  },
  {
    id: 's3',
    nombre: 'Las Polvillas Acapulco',
    direccion: 'Martín Alonso Pinzón 52, Fracc. Magallanes, 39670 Acapulco de Juárez, Gro.',
    telefono: '527445859008',
    whatsapp: '525653712507',
    horario: null,
    icon: 'store',
    maps_embed_url: 'https://maps.google.com/maps?q=Martin+Alonso+Pinzon+52+Fracc+Magallanes+Acapulco+de+Juarez&output=embed&z=16',
    maps_link_url:  'https://maps.app.goo.gl/8hNiaAjb7MTNE21KA?g_st=iw',
    badge: null,
    is_active: true,
    sort_order: 30,
  },
  {
    id: 's4',
    nombre: 'Las Polvillas Global Foods',
    direccion: 'Olvera 71, La Merced, Zona Centro, Venustiano Carranza, 15100 Ciudad de México, CDMX',
    telefono: '525545219298',
    whatsapp: '525653712507',
    horario: 'Lun–Vie: 9:00am – 7:00pm',
    icon: 'building',
    maps_embed_url: 'https://maps.google.com/maps?q=Olvera+71+La+Merced+Venustiano+Carranza+CDMX&output=embed&z=16',
    maps_link_url:  'https://maps.app.goo.gl/XaMDTyjUKA54YowB9?g_st=iw',
    badge: null,
    is_active: true,
    sort_order: 40,
  },
  {
    id: 's6',
    nombre: 'Las Polvillas Calle 7',
    direccion: 'Ciudad de México',
    telefono: null,
    whatsapp: '525653712507',
    horario: null,
    icon: 'store',
    maps_embed_url: 'https://maps.google.com/maps?q=19.4032114,-99.057842&output=embed&z=16',
    maps_link_url:  'https://maps.app.goo.gl/dxbJKhvDjaKTwe666',
    badge: null,
    is_active: true,
    sort_order: 45,
  },
  {
    id: 's5',
    nombre: 'Empacadora Las Polvillas',
    direccion: 'Planta principal de empacado · Mayoreo con cita previa',
    telefono: '525653712507',
    whatsapp: '525653712507',
    horario: 'Lun–Vie: 8:00am – 6:00pm · Cita previa requerida',
    icon: 'cube',
    maps_embed_url: 'https://maps.google.com/maps?q=Empacadora+Las+Polvillas+Del+Rio+No.5+El+Gavillero+Nicolas+Romero&output=embed&z=16',
    maps_link_url:  'https://maps.app.goo.gl/5zXc4K9eGFj5fQ518?g_st=iw',
    badge: null,
    is_active: true,
    sort_order: 50,
  },
];

// 3 vacantes idénticas al demo
export const DEMO_VACANCIES: Vacancy[] = [
  {
    id: 'v1',
    titulo: 'Operador de Producción',
    area: 'Planta',
    tipo: 'Tiempo completo',
    descripcion: 'Apoyo en el proceso de elaboración de embutidos. Experiencia en alimentos deseable. No indispensable.',
    activa: true,
    created_at: '2024-01-01T00:00:00Z',
    updated_at: '2024-01-01T00:00:00Z',
  },
  {
    id: 'v2',
    titulo: 'Repartidor / Chofer',
    area: 'Logística',
    tipo: 'Tiempo completo',
    descripcion: 'Distribución de pedidos en zona metropolitana. Licencia tipo B indispensable. Conocimiento de rutas.',
    activa: true,
    created_at: '2024-01-01T00:00:00Z',
    updated_at: '2024-01-01T00:00:00Z',
  },
  {
    id: 'v3',
    titulo: 'Vendedor de Mostrador',
    area: 'Ventas',
    tipo: 'Medio tiempo',
    descripcion: 'Atención al cliente en puntos de venta. Actitud de servicio, manejo de efectivo y caja.',
    activa: true,
    created_at: '2024-01-01T00:00:00Z',
    updated_at: '2024-01-01T00:00:00Z',
  },
];
